import { useState } from 'react'
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react'
import { jsonFetch } from '@/lib/api/jsonFetch'
import { Button } from '@/components/ui'

export interface HelpFeedbackFormProps {
  section: string
  slug: string
  title: string
}

/**
 * "Was this helpful?" footer on every help detail page.
 *
 * Writes through the existing `save_feedback` API path used by the
 * CARLA chat surface. Discriminated by `source: "help_page"` +
 * `page_path: "<section>/<slug>"` so an admin browsing the
 * carla_feedback table can filter help feedback separately.
 */
export function HelpFeedbackForm({ section, slug, title }: HelpFeedbackFormProps) {
  const [rating, setRating] = useState<'helpful' | 'unhelpful' | null>(null)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit() {
    if (!rating) return
    setSubmitting(true)
    setError(null)
    try {
      await jsonFetch('/api/feedback', {
        method: 'POST',
        body: {
          source: 'help_page',
          page_path: `${section}/${slug}`,
          page_title: title,
          rating,
          comment: comment.trim() || undefined,
        },
      })
      setSubmitted(true)
    } catch (e) {
      setError((e as Error).message || 'Could not save feedback.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="flex items-center gap-2 rounded-sm border border-success-border bg-success-bg px-3 py-2 text-sm text-success-text">
        <Check size={14} />
        Thanks — your feedback was saved.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-primary">Was this helpful?</span>
        <button
          type="button"
          onClick={() => setRating('helpful')}
          aria-label="Helpful"
          aria-pressed={rating === 'helpful'}
          className={
            rating === 'helpful'
              ? 'inline-flex items-center gap-1 rounded-xs border border-success-border bg-success-bg px-2 py-1 text-xs font-semibold text-success-text'
              : 'inline-flex items-center gap-1 rounded-xs border border-border-default bg-surface px-2 py-1 text-xs text-secondary hover:bg-surface-hover'
          }
        >
          <ThumbsUp size={12} />
          Yes
        </button>
        <button
          type="button"
          onClick={() => setRating('unhelpful')}
          aria-label="Not helpful"
          aria-pressed={rating === 'unhelpful'}
          className={
            rating === 'unhelpful'
              ? 'inline-flex items-center gap-1 rounded-xs border border-danger-border bg-danger-bg px-2 py-1 text-xs font-semibold text-danger-text'
              : 'inline-flex items-center gap-1 rounded-xs border border-border-default bg-surface px-2 py-1 text-xs text-secondary hover:bg-surface-hover'
          }
        >
          <ThumbsDown size={12} />
          No
        </button>
      </div>

      {rating ? (
        <>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={
              rating === 'unhelpful'
                ? 'What would have made this page more useful? (optional)'
                : 'Anything else worth noting? (optional)'
            }
            rows={3}
            className="w-full rounded-sm border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-accent focus:outline-none"
          />
          {error ? (
            <div className="text-xs text-danger-text">{error}</div>
          ) : null}
          <Button variant="primary" size="sm" onClick={handleSubmit} loading={submitting}>
            Send feedback
          </Button>
        </>
      ) : null}
    </div>
  )
}
